/**
 * useFlows — list + mutations for the Hive Flows hub.
 *
 * Hierarchical query keys for fine-grained invalidation, mirroring the
 * automations feature conventions.
 */

import {
  type Flow,
  type FlowCreatePayload,
  type FlowUpdatePayload,
  type FlowVisibility,
  createFlow,
  deleteFlow,
  duplicateFlow,
  listFlows,
  listTeamFlows,
  shareFlow,
  updateFlow,
} from "@ando/core";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const root = ["hive-flows"] as const;

export const flowsQueryKeys = {
  root,
  list: (status?: string) => ["hive-flows", "list", status ?? null] as const,
  detail: (id: string) => ["hive-flows", "flow", id] as const,
  team: ["hive-flows", "team"] as const,
  /** Run-scoped key — consumed by the operator run surface (``run/hooks``). */
  run: (runId: string) => [...root, "run", runId] as const,
};

/** Shared list query — hook + `/workspace/flows` route loader (intent preload). */
export const flowsListQueryOptions = (status?: string) =>
  queryOptions({
    queryKey: flowsQueryKeys.list(status),
    queryFn: () => listFlows({ status }),
    staleTime: 30_000,
  });

export function useFlowsList(status?: string) {
  const query = useQuery(flowsListQueryOptions(status));
  return {
    flows: query.data?.flows ?? ([] as Flow[]),
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FlowCreatePayload) => createFlow(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowsQueryKeys.root });
    },
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flowId: string) => deleteFlow(flowId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowsQueryKeys.root });
    },
  });
}

export function useUpdateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, payload }: { flowId: string; payload: FlowUpdatePayload }) =>
      updateFlow(flowId, payload),
    onSuccess: (flow) => {
      qc.setQueryData(flowsQueryKeys.detail(flow.flow_id), flow);
      void qc.invalidateQueries({ queryKey: flowsQueryKeys.root });
    },
  });
}

// ── Team sharing (issue #936) ────────────────────────────────────────────────
export function useTeamFlows() {
  const query = useQuery({
    queryKey: flowsQueryKeys.team,
    queryFn: () => listTeamFlows(),
    staleTime: 30_000,
  });
  return {
    flows: query.data?.flows ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useShareFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ flowId, visibility }: { flowId: string; visibility: FlowVisibility }) =>
      shareFlow(flowId, visibility),
    onSuccess: (flow) => {
      qc.setQueryData(flowsQueryKeys.detail(flow.flow_id), flow);
      // Own list shows the visibility badge; the team catalog gains/loses a row.
      void qc.invalidateQueries({ queryKey: flowsQueryKeys.root });
      void qc.invalidateQueries({ queryKey: flowsQueryKeys.team });
    },
  });
}

export function useDuplicateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (flowId: string) => duplicateFlow(flowId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: flowsQueryKeys.root });
    },
  });
}
